/**
 * L'onglet Contacts d'une fiche d'annuaire : la liste, et de quoi la changer.
 *
 * Trois routes derrière, toutes sous la fiche :
 * `POST /<annuaire>/:id/contacts`, `PATCH` et `DELETE` de
 * `/<annuaire>/:id/contacts/:contactId`. Elles rendent toutes la fiche
 * complète, si bien que l'écran n'a jamais à recharger après avoir écrit.
 *
 * Ce composant ne dépend d'aucun annuaire : un contact de transporteur porte
 * les mêmes colonnes qu'un contact de client, et le chemin est construit par
 * l'écran qui l'entoure.
 *
 * Ce composant ne fait que dessiner. Il ne valide rien et n'appelle rien :
 * `validerContact` est une fonction pure, les appels sont dans l'écran. C'est
 * ce qui permet de le rendre et de le regarder dans n'importe quel état.
 *
 * **La confirmation de suppression est posée SUR LA CARTE**, et non dans une
 * fenêtre du navigateur : un `confirm()` bloque la page, et ne se relit pas.
 * Ici la question reste sous les yeux, à côté du nom qu'elle concerne.
 */

import {
  CHAMPS_CONTACT,
  type CleChampContact,
  type SaisieContact,
} from './annuaire.js';
import type { Contact } from './api.js';

export type EditionContact =
  | { readonly nom: 'aucune' }
  | {
      readonly nom: 'creation' | 'modification';
      readonly id: string | null;
      readonly initiale: SaisieContact;
      readonly saisie: SaisieContact;
      readonly fautes: Readonly<Partial<Record<CleChampContact, string>>>;
      readonly refus: string | null;
      readonly envoi: boolean;
    }
  | {
      readonly nom: 'suppression';
      readonly id: string;
      readonly envoi: boolean;
      readonly refus: string | null;
    };

interface Proprietes {
  readonly contacts: readonly Contact[];
  readonly edition: EditionContact;
  readonly quandAjouter: () => void;
  readonly quandModifier: (contact: Contact) => void;
  readonly quandDemanderSuppression: (id: string) => void;
  readonly quandChamp: (cle: CleChampContact, valeur: string) => void;
  readonly quandPrincipal: (coche: boolean) => void;
  readonly quandEnregistrer: () => void;
  readonly quandSupprimer: () => void;
  readonly quandAnnuler: () => void;
}

function Formulaire({
  edition,
  quandChamp,
  quandPrincipal,
  quandEnregistrer,
  quandAnnuler,
}: {
  readonly edition: Extract<EditionContact, { nom: 'creation' | 'modification' }>;
  readonly quandChamp: (cle: CleChampContact, valeur: string) => void;
  readonly quandPrincipal: (coche: boolean) => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
}) {
  return (
    <div className="section">
      <div className="section-titre">
        <span>
          {edition.nom === 'creation' ? 'Nouveau contact' : 'Modifier le contact'}
        </span>
      </div>

      {edition.refus !== null && <p className="bandeau">{edition.refus}</p>}

      <form
        className="formulaire"
        onSubmit={(e) => {
          e.preventDefault();
          quandEnregistrer();
        }}
      >
        {CHAMPS_CONTACT.map((champ) => {
          const faute = edition.fautes[champ.cle];
          const identifiant = `contact-${champ.cle}`;
          const classes = ['champ'];
          if ('chiffre' in champ && champ.chiffre) classes.push('code');
          if (faute !== undefined) classes.push('fautif');

          return (
            <div
              className={
                'court' in champ && champ.court ? 'bloc-champ court' : 'bloc-champ'
              }
              key={champ.cle}
            >
              <label className="etiquette" htmlFor={identifiant}>
                {champ.etiquette}
                {'obligatoire' in champ && champ.obligatoire && (
                  <span className="sourdine"> · obligatoire</span>
                )}
              </label>
              {'long' in champ && champ.long ? (
                <textarea
                  className={classes.join(' ')}
                  id={identifiant}
                  rows={2}
                  value={edition.saisie[champ.cle]}
                  disabled={edition.envoi}
                  aria-describedby={
                    faute === undefined ? undefined : `${identifiant}-err`
                  }
                  onChange={(e) => quandChamp(champ.cle, e.target.value)}
                />
              ) : (
                <input
                  className={classes.join(' ')}
                  id={identifiant}
                  value={edition.saisie[champ.cle]}
                  disabled={edition.envoi}
                  aria-describedby={
                    faute === undefined ? undefined : `${identifiant}-err`
                  }
                  onChange={(e) => quandChamp(champ.cle, e.target.value)}
                />
              )}
              {faute !== undefined && (
                <span className="erreur-champ" id={`${identifiant}-err`}>
                  {faute}
                </span>
              )}
            </div>
          );
        })}

        {/* Une case, et non un champ : `principal` est un oui/non. La phrase
            dit la conséquence, parce qu'elle n'est pas devinable — cocher ici
            décoche l'autre. */}
        <div className="bloc-champ case">
          <label className="etiquette-case">
            <input
              type="checkbox"
              checked={edition.saisie.principal}
              disabled={edition.envoi}
              onChange={(e) => quandPrincipal(e.target.checked)}
            />
            Contact principal
          </label>
          <span className="sourdine aide-case">
            Celui qu’on appelle en premier. Un seul par fiche : le cocher ici décoche
            l’autre.
          </span>
        </div>

        <div className="actions">
          <button className="bouton" type="submit" disabled={edition.envoi}>
            {edition.envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={edition.envoi}
            onClick={quandAnnuler}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}

export function OngletContacts({
  contacts,
  edition,
  quandAjouter,
  quandModifier,
  quandDemanderSuppression,
  quandChamp,
  quandPrincipal,
  quandEnregistrer,
  quandSupprimer,
  quandAnnuler,
}: Proprietes) {
  const ecrit = edition.nom === 'creation' || edition.nom === 'modification';

  return (
    <div className="barre-onglet">
      {!ecrit && (
        <div className="actions">
          <button
            className="bouton bouton-discret"
            type="button"
            onClick={quandAjouter}
          >
            Ajouter un contact
          </button>
        </div>
      )}

      {ecrit && (
        <Formulaire
          edition={edition}
          quandChamp={quandChamp}
          quandPrincipal={quandPrincipal}
          quandEnregistrer={quandEnregistrer}
          quandAnnuler={quandAnnuler}
        />
      )}

      {contacts.length === 0 ? (
        <p className="aucun-contact">Aucun contact enregistré.</p>
      ) : (
        <div className="contacts">
          {contacts.map((contact) => {
            const enSuppression =
              edition.nom === 'suppression' && edition.id === contact.id;
            const enModification =
              edition.nom === 'modification' && edition.id === contact.id;

            return (
              <div
                className={enModification ? 'contact en-cours' : 'contact'}
                key={contact.id}
              >
                <span className="contact-nom">
                  {contact.nom}
                  {contact.principal && <span className="pastille">principal</span>}
                </span>
                {contact.fonction !== null && (
                  <span className="contact-role">{contact.fonction}</span>
                )}
                {/* Les deux numéros portent leur étiquette : deux suites de
                    chiffres l'une sous l'autre ne diraient pas laquelle joint
                    la personne sur la route. */}
                {contact.telephone !== null && (
                  <span className="contact-joindre">
                    <span className="etiquette-numero">tél.</span> {contact.telephone}
                  </span>
                )}
                {contact.mobile !== null && (
                  <span className="contact-joindre">
                    <span className="etiquette-numero">port.</span> {contact.mobile}
                  </span>
                )}
                {contact.courriel !== null && (
                  <span className="contact-joindre">{contact.courriel}</span>
                )}
                {contact.note !== null && (
                  <span className="contact-note">{contact.note}</span>
                )}

                {enSuppression ? (
                  <div className="confirmation">
                    <p className="empechement">
                      Supprimer {contact.nom} ? Cette action est sans retour.
                    </p>
                    {edition.refus !== null && (
                      <p className="bandeau">{edition.refus}</p>
                    )}
                    <div className="actions">
                      <button
                        className="bouton bouton-risque"
                        type="button"
                        disabled={edition.envoi}
                        onClick={quandSupprimer}
                      >
                        {edition.envoi ? 'Suppression…' : 'Supprimer'}
                      </button>
                      <button
                        className="bouton bouton-discret"
                        type="button"
                        disabled={edition.envoi}
                        onClick={quandAnnuler}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  !ecrit && (
                    <div className="actions actions-carte">
                      <button
                        className="bouton bouton-discret"
                        type="button"
                        onClick={() => quandModifier(contact)}
                      >
                        Modifier
                      </button>
                      <button
                        className="bouton bouton-risque"
                        type="button"
                        onClick={() => quandDemanderSuppression(contact.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
